import {
  Box,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import type { Dayjs } from "dayjs";
import type { BudgetData, BudgetPhaseForm } from "./BudgetSelector";
import {
  ScheduledPhaseRangePicker,
  type PhaseCalendarNeighborBounds,
} from "./ScheduledPhaseRangePicker";
import {
  guidedBudgetFieldBorder,
  guidedBudgetFieldShadow,
  guidedBudgetPrimaryColor,
  phaseBudgetInputProps,
  phaseRowDeleteIconMinW,
  phaseScheduledFieldShellMinH,
} from "./guidedBudgetEditorTokens";
import WhiteBgInput from "../../WhiteBgInput";
import { SCHEDULED_BUDGET_FIRST_DELETABLE_PHASE_INDEX } from "./guidedBudgetEditorMoney";
import { GuidedBudgetCurrencyInput } from "./GuidedBudgetCurrencyInput";
import {
  clampScheduledPhaseWallPatch,
  type ScheduledBudgetAdDeliveryWindowUtc,
} from "../../utils/eventWallTime";

type GuidedBudgetPhaseRowProps = {
  index: number;
  segment: BudgetPhaseForm;
  segmentCount: number;
  eventTimezone: string;
  deliveryWindowInEventTimezone: { start: Dayjs; end: Dayjs } | null;
  neighborPickerBounds: PhaseCalendarNeighborBounds | null;
  scheduledBudgetDeliveryWindow: ScheduledBudgetAdDeliveryWindowUtc | null;
  /** Linked Meta campaign and this phase has ended — read-only row. */
  isCompletedPhase?: boolean;
  /** Linked Meta: boosted phase cannot POST after advance + 3h filter — read-only row. */
  isLockedPhase?: boolean;
  lockReason?: string;
  /** Linked Meta: phase start cannot be before now (uses {@link eventTimezone}). */
  capPhaseStartAtNow?: boolean;
  budget?: BudgetData;
  /** RHF / Yup message for this row only (Edit budget modal). */
  rowValidationMessage?: string | null;
  invalidTimeField?: "start" | "end" | null;
  invalidAmount?: boolean;
  onChangeSegment?: (index: number, patch: Partial<BudgetPhaseForm>) => void;
  onRemoveSegment?: (index: number) => void;
};

export function GuidedBudgetPhaseRow({
  index,
  segment: s,
  segmentCount,
  eventTimezone,
  deliveryWindowInEventTimezone,
  neighborPickerBounds,
  scheduledBudgetDeliveryWindow,
  isCompletedPhase = false,
  isLockedPhase = false,
  lockReason,
  capPhaseStartAtNow = false,
  budget,
  rowValidationMessage,
  invalidTimeField,
  invalidAmount,
  onChangeSegment,
  onRemoveSegment,
}: GuidedBudgetPhaseRowProps) {
  const isReadOnlyPhase = isCompletedPhase || isLockedPhase;
  /** First two phases (rows 1–2, indexes 0–1) are mandatory; delete control from row 3 onward only. */
  const canDeleteThisPhase =
    !isReadOnlyPhase && index >= SCHEDULED_BUDGET_FIRST_DELETABLE_PHASE_INDEX;
  const isFirstPhase = index === 0;
  const isLastPhase = index === segmentCount - 1;

  return (
    <VStack align="stretch" spacing={1.5} w="full">
      <Text
        fontSize="md"
        fontWeight="semibold"
        color={guidedBudgetPrimaryColor}
        lineHeight="1.3"
      >
        Phase {index + 1}
        {isCompletedPhase ? (
          <Text
            as="span"
            ml={2}
            fontSize="xs"
            fontWeight="medium"
            color="gray.500"
          >
            Completed
          </Text>
        ) : isLockedPhase ? (
          <Text
            as="span"
            ml={2}
            fontSize="xs"
            fontWeight="medium"
            color="gray.500"
          >
            Locked on Meta
          </Text>
        ) : null}
      </Text>
      <Grid
        w="full"
        minW={0}
        templateColumns={{
          base: "minmax(0,1fr)",
          md: "minmax(0,1fr) auto",
        }}
        gap={6}
        alignItems="stretch"
      >
        <GridItem
          minW={0}
          w="full"
          display="flex"
          alignItems="center"
          minH={phaseScheduledFieldShellMinH}
        >
          <ScheduledPhaseRangePicker
            phaseIndex={index}
            eventTimezone={eventTimezone}
            time_start={s.time_start}
            time_end={s.time_end}
            deliveryWindowInEventTimezone={deliveryWindowInEventTimezone}
            neighborPickerBounds={neighborPickerBounds}
            isFirstPhase={isFirstPhase}
            isLastPhase={isLastPhase}
            isCompletedPhase={isReadOnlyPhase}
            capPhaseStartAtNow={capPhaseStartAtNow}
            invalidTimeField={invalidTimeField}
            onChangeStart={(wall) =>
              onChangeSegment?.(
                index,
                clampScheduledPhaseWallPatch(
                  s,
                  { time_start: wall },
                  eventTimezone,
                  scheduledBudgetDeliveryWindow
                )
              )
            }
            onChangeEnd={(wall) =>
              onChangeSegment?.(
                index,
                clampScheduledPhaseWallPatch(
                  s,
                  { time_end: wall },
                  eventTimezone,
                  scheduledBudgetDeliveryWindow
                )
              )
            }
          />
        </GridItem>

        <GridItem
          minW={0}
          w={{ base: "full", md: "auto" }}
          position="relative"
          zIndex={1}
        >
          <HStack
            spacing={2}
            align="stretch"
            w={{ base: "full", md: "auto" }}
            justify={{ base: "flex-start", md: "flex-end" }}
            minH={phaseScheduledFieldShellMinH}
          >
            <Box
              w={{ base: "full", md: "104px" }}
              minW={{ base: "full", md: "104px" }}
              maxW={{ base: "full", md: "104px" }}
              px={3}
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              minH={phaseScheduledFieldShellMinH}
              borderRadius="md"
              borderWidth={invalidAmount ? "2px" : "1px"}
              borderColor={invalidAmount ? "red.400" : guidedBudgetFieldBorder}
              bg="white"
              boxShadow={guidedBudgetFieldShadow}
            >
              <HStack
                spacing={2.5}
                align="center"
                justify="flex-start"
                lineHeight="1.25"
                fontSize="md"
                letterSpacing="-0.48px"
                w="full"
              >
                <Text
                  as="span"
                  flexShrink={0}
                  fontWeight="medium"
                  color="rgba(34,34,34,0.6)"
                >
                  $
                </Text>
                <Box flex={1} minW={0}>
                  <GuidedBudgetCurrencyInput
                    customInput={WhiteBgInput}
                    aria-label={`Phase ${index + 1} budget`}
                    amount={s.amount?.amount}
                    isDisabled={isReadOnlyPhase}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    minW="3.5rem"
                    maxW="100%"
                    w="auto"
                    letterSpacing="-0.36px"
                    fontWeight="normal"
                    color="gray.800"
                    {...phaseBudgetInputProps}
                    onCommit={(next) =>
                      onChangeSegment?.(index, {
                        amount: {
                          amount: next,
                          currency:
                            s.amount?.currency ?? budget?.amount?.currency ?? "USD",
                        },
                      })
                    }
                  />
                </Box>
              </HStack>
            </Box>
            {canDeleteThisPhase ? (
              <IconButton
                aria-label="Remove phase"
                icon={<DeleteIcon />}
                variant="outline"
                borderColor={guidedBudgetFieldBorder}
                borderRadius="md"
                alignSelf="stretch"
                h="auto"
                minW={phaseRowDeleteIconMinW}
                flexShrink={0}
                onClick={() => onRemoveSegment?.(index)}
              />
            ) : null}
          </HStack>
        </GridItem>
      </Grid>
      {isLockedPhase && lockReason ? (
        <Text fontSize="sm" color="gray.600" lineHeight="short" px={0.5}>
          {lockReason}
        </Text>
      ) : null}
      {rowValidationMessage ? (
        <Text
          fontSize="sm"
          color="red.600"
          lineHeight="short"
          px={0.5}
          role="alert"
        >
          {rowValidationMessage}
        </Text>
      ) : null}
    </VStack>
  );
}
